// progressStore.js
import { defineStore } from 'pinia'

export enum LoadingType {
  FetchAllTransactions = 'fetch-all-transactions',
  FetchTransaction = 'fetch-transaction',
  UpdateTransaction = 'update-transaction',
  FetchAllCategories = 'fetch-all-categories',
  FetchCategory = 'fetch-category',
  PostCategory = 'post-category',
  ResetBudget = 'reset-budget',
  Signup = 'signup',
  Login = 'login',
  Logout = 'logout',
  ReconnectAccount = 'reconnect-account',
  DeleteAccount = 'delete-account',
  AddAccount = 'add-account',
  FetchAccounts = 'fetch-accounts',
  fetchProfiles = 'fetch-profiles',
  RefreshData = 'refresh-data',
}

export type RefreshDataStatus = 'idle' | 'refreshing' | 'success' | 'error'

let refreshDataStatusTimeout: ReturnType<typeof setTimeout> | null = null

export const useLoadingStore = defineStore('loading', {
  state: () => ({
    loadingTypes: [] as LoadingType[],
    loadingCounts: {} as Partial<Record<LoadingType, number>>,
    refreshDataStatus: 'idle' as RefreshDataStatus,
  }),
  getters: {
    isLoading: (state) => (loadingType?: LoadingType) => {
      return loadingType
        ? (state.loadingCounts[loadingType] ?? 0) > 0
        : Object.values(state.loadingCounts).some((count) => (count ?? 0) > 0)
    },
  },
  actions: {
    startLoading(loadingType: LoadingType) {
      this.loadingCounts[loadingType] =
        (this.loadingCounts[loadingType] ?? 0) + 1
      if (!this.loadingTypes.includes(loadingType)) {
        this.loadingTypes.push(loadingType)
      }
    },
    finishLoading(loadingType: LoadingType) {
      const nextCount = Math.max((this.loadingCounts[loadingType] ?? 0) - 1, 0)

      if (nextCount > 0) {
        this.loadingCounts[loadingType] = nextCount
        return
      }

      delete this.loadingCounts[loadingType]
      this.loadingTypes = this.loadingTypes.filter((t) => t !== loadingType)
    },
    startRefreshData() {
      if (refreshDataStatusTimeout) {
        clearTimeout(refreshDataStatusTimeout)
        refreshDataStatusTimeout = null
      }

      this.refreshDataStatus = 'refreshing'
      this.startLoading(LoadingType.RefreshData)
    },
    finishRefreshData(
      status: Exclude<RefreshDataStatus, 'idle' | 'refreshing'>,
    ) {
      this.finishLoading(LoadingType.RefreshData)

      if (this.isLoading(LoadingType.RefreshData)) {
        return
      }

      this.refreshDataStatus = status
      refreshDataStatusTimeout = setTimeout(() => {
        this.refreshDataStatus = 'idle'
        refreshDataStatusTimeout = null
      }, 900)
    },
  },
})
